import type { InstrumentRunAssayVersion, InstrumentRunDataRow } from '@/types';

export type ControlRow = Required<
  Pick<InstrumentRunDataRow, 'name' | 'well_position' | 'demux_index' | 'flow_cell' | 'lane'>
> & {
  is_control: true;
  control_type: string;
  study_name: string;
  org_name: string;
  library_group: string;
  sample_group: string;
};

const CONTROL_STUDY_NAME = 'Control';
const CONTROL_SAMPLE_GROUP = 'Control';
const LP_CONTROL_2_NAME = 'Library Prep Control 2';
const LP_CONTROL_2_TYPE = 'Library Prep Control 2';

function makeBaseRows(ownerOrgName: string): ControlRow[] {
  return [1, 2, 3, 4].flatMap(lane =>
    [
      { well_position: 'A1', demux_index: 1 },
      { well_position: 'A2', demux_index: 2 },
      { well_position: 'A3', demux_index: 3 },
    ].map(({ well_position, demux_index }) => ({
      name: 'Library Prep Control',
      flow_cell: '1',
      lane,
      well_position,
      demux_index,
      is_control: true as const,
      control_type: 'Library Prep Control',
      study_name: CONTROL_STUDY_NAME,
      org_name: ownerOrgName,
      library_group: '',
      sample_group: CONTROL_SAMPLE_GROUP,
    })),
  );
}

/** A4 "Library Prep Control 2" row. `populated=false` clears the data columns
 *  (Sample/Study/Org/Library Group/Sample Group) while keeping the structural
 *  Flowcell/Lane/Well/Demux so the row stays visible (POR-356, 12-sample not-accepted). */
function makeA4Row(populated: boolean, ownerOrgName: string): ControlRow {
  return {
    name: populated ? LP_CONTROL_2_NAME : '',
    flow_cell: '1',
    lane: 4,
    well_position: 'A4',
    demux_index: 2,
    is_control: true,
    control_type: LP_CONTROL_2_TYPE,
    study_name: populated ? CONTROL_STUDY_NAME : '',
    org_name: populated ? ownerOrgName : '',
    library_group: '',
    sample_group: populated ? CONTROL_SAMPLE_GROUP : '',
  };
}

/**
 * Derive the read-only control rows for the Controls table.
 * - 24-sample Tau: A4 required (always populated), `useA4` ignored.
 * - 12-sample Tau: A4 always visible; populated only when `useA4` is true.
 * - other Tau counts (4, 36): no A4.
 * - non-Tau: no controls.
 */
export function getControlRows(
  assayVersion: InstrumentRunAssayVersion,
  numSamples: number,
  useA4: boolean,
  ownerOrgName: string,
): ControlRow[] {
  if (assayVersion !== 'Tau') return [];
  const rows = makeBaseRows(ownerOrgName);
  if (numSamples === 24) {
    rows.push(makeA4Row(true, ownerOrgName));
  } else if (numSamples === 12) {
    rows.push(makeA4Row(useA4, ownerOrgName));
  }
  return rows;
}
